import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { AccountBalance as FeesIcon, Payment as PaymentIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { decryptAppId, decryptText } from '../../utils/encryption';
import DoApiService from '../../services/DoApiService';
import authService from '../../services/authService';
import paymentService from '../../services/paymentService';

const formatAmount = (val) =>
  Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SectionHeader = ({ title, color = '#00024c' }) => (
  <Box sx={{ bgcolor: color, px: 2, py: 1, borderRadius: '4px 4px 0 0' }}>
    <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 700 }}>
      {title}
    </Typography>
  </Box>
);

const UpfrontFees = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feesData, setFeesData] = useState(null);
  const [deductTDS, setDeductTDS] = useState(false);
  const [tanNumber, setTanNumber] = useState('');
  const [tanError, setTanError] = useState('');
  const [payLoading, setPayLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const encAppId = searchParams.get('appId');
        if (!encAppId) {
          setError('Application ID missing');
          setLoading(false);
          return;
        }

        const appId = decryptAppId(encAppId);
        if (!appId) {
          setError('Invalid application ID');
          setLoading(false);
          return;
        }

        const response = await DoApiService.getUpfrontFees(appId);
        if (response.success) {
          setFeesData(response.data);
        } else {
          setError(response.message || 'Failed to load fees data');
        }
      } catch (err) {
        setError('Error loading fees data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [searchParams]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const { assessmentFees, annualAccreditationFees, combined, laboratoryName, appId, orgId, email: labEmail, mobile: labMobile } = feesData || {};

  const calculateTDS = () => (combined?.grossTotal || 0) * 0.02;
  const getFinalAmount = () => (combined?.grandTotal || 0) - calculateTDS();

  const handlePay = async () => {
    if (deductTDS && !tanNumber.trim()) {
      setTanError('TAN number is required');
      return;
    }
    if (deductTDS && !/^[A-Z]{4}[0-9]{5}[A-Z]$/.test(tanNumber)) {
      setTanError('Invalid TAN format (e.g., ABCD12345E)');
      return;
    }

    setPayLoading(true);
    try {
      const userSession = authService.getUserData();
      const effectiveOrgId = orgId
        || localStorage.getItem('orgId')
        || userSession?.orgId
        || userSession?.OrgId;
      const effectiveUserName = localStorage.getItem('username')
        || userSession?.username
        || userSession?.name
        || userSession?.UserName
        || '';
      const tdsAmount = deductTDS ? calculateTDS() : 0;
      const finalAmount = deductTDS ? getFinalAmount() : combined?.grandTotal || 0;

      console.log('Upfront pay payload debug:', {
        appId, orgId: effectiveOrgId, amount: combined?.grossTotal,
        totalAmount: finalAmount, userName: effectiveUserName
      });

      const result = await paymentService.initiateUpfrontPayment({
        appId,
        orgId: effectiveOrgId,
        assessmentAmount: assessmentFees?.summary?.grandTotal || 0,
        aafeeAmount: annualAccreditationFees?.grandTotal || 0,
        assessmentGrossAmount: assessmentFees?.summary?.grossTotal || 0,
        assessmentGstAmount: assessmentFees?.summary?.gstAmount || 0,
        assessmentGrandTotal: assessmentFees?.summary?.grandTotal || 0,
        aafeeGrossAmount: annualAccreditationFees?.amount || 0,
        aafeeGstAmount: annualAccreditationFees?.gstAmount || 0,
        aafeeGrandTotal: annualAccreditationFees?.grandTotal || 0,
        amount: combined?.grossTotal || 0,
        gstAmount: combined?.gstAmount || 0,
        totalAmount: finalAmount,
        currency: 'INR',
        email: labEmail || '',
        mobile: labMobile || '',
        labTypeId: '3',
        chkDeductTds: deductTDS,
        TDSAmount: tdsAmount,
        TDSPercent: deductTDS ? 2 : 0,
        tan: tanNumber,
        userName: effectiveUserName,
      });

      if (result.success && result.data?.paymentUrl) {
        localStorage.setItem('currentOrderId', result.data.orderId);
        localStorage.setItem('paymentInitiated', JSON.stringify({
          orderId: result.data.orderId,
          amount: result.data.amount,
          appId,
          orgId: effectiveOrgId,
          timestamp: new Date().toISOString(),
        }));
        window.location.href = result.data.paymentUrl;
      } else {
        throw new Error(result.message || 'Failed to initiate payment');
      }
    } catch (err) {
      alert('Payment initiation failed: ' + err.message);
    } finally {
      setPayLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f5f6fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Page Title */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #00024c 0%, #1a237e 100%)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FeesIcon sx={{ color: '#fff', fontSize: 36 }} />
            <Box>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                Upfront Fees
              </Typography>
              {laboratoryName && (
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
                  {laboratoryName}
                </Typography>
              )}
              <Chip
                label={`App ID: ${appId}`}
                size="small"
                sx={{ mt: 0.5, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '0.75rem' }}
              />
            </Box>
          </Box>
        </Paper>

  

        {/* Combined Total */}
        <Paper elevation={3} sx={{ overflow: 'hidden' }}>
          <SectionHeader title="Combined Total (Assessment + Annual Accreditation Fees)" color="#1565c0" />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Fee Component</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Gross Amount (₹)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>GST (₹)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Grand Total (₹)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover>
                  <TableCell>Assessment Fees</TableCell>
                  <TableCell align="right">₹ {formatAmount(assessmentFees?.summary?.grossTotal)}</TableCell>
                  <TableCell align="right">₹ {formatAmount(assessmentFees?.summary?.gstAmount)}</TableCell>
                  <TableCell align="right">₹ {formatAmount(assessmentFees?.summary?.grandTotal)}</TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell>Annual Accreditation Fees</TableCell>
                  <TableCell align="right">₹ {formatAmount(annualAccreditationFees?.amount)}</TableCell>
                  <TableCell align="right">₹ {formatAmount(annualAccreditationFees?.gstAmount)}</TableCell>
                  <TableCell align="right">₹ {formatAmount(annualAccreditationFees?.grandTotal)}</TableCell>
                </TableRow>
                <Divider />
                <TableRow sx={{ bgcolor: '#bbdefb' }}>
                  <TableCell sx={{ fontWeight: 800, fontSize: '1rem' }}>Total Fees Payable</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: '1rem' }}>₹ {formatAmount(combined?.grossTotal)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: '1rem' }}>₹ {formatAmount(combined?.gstAmount)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: '1rem', color: '#c62828' }}>
                    ₹ {formatAmount(combined?.grandTotal)}
                  </TableCell>
                </TableRow>
                {deductTDS && (
                  <TableRow>
                    <TableCell sx={{ color: 'warning.main' }}>TDS Deduction (2%)</TableCell>
                    <TableCell align="right" sx={{ color: 'warning.main' }}>-₹ {formatAmount(calculateTDS())}</TableCell>
                    <TableCell />
                    <TableCell align="right" sx={{ color: 'warning.main' }}>-₹ {formatAmount(calculateTDS())}</TableCell>
                  </TableRow>
                )}
                {deductTDS && (
                  <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                    <TableCell sx={{ fontWeight: 800, fontSize: '1rem' }}>Final Amount Payable (After TDS)</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 800, fontSize: '1rem', color: '#2e7d32' }}>
                      ₹ {formatAmount(getFinalAmount())}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* TDS + Pay Section */}
          <Box sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
            <Box sx={{ p: 2, bgcolor: '#fff8e1', borderRadius: 1, mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={deductTDS}
                    onChange={(e) => { setDeductTDS(e.target.checked); setTanError(''); }}
                    color="warning"
                  />
                }
                label="Deduct TDS (2%)"
              />
              {deductTDS && (
                <Box sx={{ mt: 1 }}>
                  <TextField
                    label="TAN Number"
                    value={tanNumber}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase();
                      setTanNumber(val);
                      if (val && !/^[A-Z]{4}[0-9]{5}[A-Z]$/.test(val)) {
                        setTanError('Invalid TAN format (e.g., ABCD12345E)');
                      } else {
                        setTanError('');
                      }
                    }}
                    size="small"
                    required
                    error={!!tanError}
                    helperText={tanError || '4 letters + 5 digits + 1 letter (e.g., ABCD12345E)'}
                    placeholder="e.g., ABCD12345E"
                    inputProps={{ maxLength: 10 }}
                    sx={{ width: 280 }}
                  />
                  <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                    TDS: ₹ {formatAmount(calculateTDS())} &nbsp;|&nbsp; Final Amount: ₹ {formatAmount(getFinalAmount())}
                  </Alert>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Amount to Pay</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1565c0' }}>
                  ₹ {formatAmount(deductTDS ? getFinalAmount() : combined?.grandTotal)}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={<PaymentIcon />}
                onClick={handlePay}
                disabled={payLoading}
                sx={{ minWidth: 200, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
              >
                {payLoading ? 'Processing...' : `Pay ₹ ${formatAmount(deductTDS ? getFinalAmount() : combined?.grandTotal)} via ICICI`}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default UpfrontFees;
